import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, CalendarIcon, ArrowRightIcon } from 'lucide-react';
const benefits = [{
  title: 'Je weet wat werkt vóórdat je bouwt',
  desc: 'Geen maanden wachten op SEO-resultaten die tegenvallen. Je bouwt alleen op bewezen data.'
}, {
  title: 'Je concurrent kan niet meer instappen',
  desc: 'Zodra CorePrint start in jouw markt, sluit die markt. Jij hebt de exclusiviteit, zij niet.'
}, {
  title: 'Je betaalt minder naarmate het groeit',
  desc: 'Zodra organisch verkeer toeneemt, schalen we de ad-uitgaven af. Groei zonder eeuwig afhankelijk te zijn van budget.'
}, {
  title: 'Je krijgt alle data, altijd',
  desc: 'Geen black boxes. Het CorePrint-rapport is van jou. Je kunt er altijd mee doen wat je wilt.'
}, {
  title: 'Zichtbaar overal waar klanten zoeken',
  desc: 'Niet alleen in Google, maar ook in AI-gedreven zoekmachines zoals ChatGPT, Perplexity en Google AI Overviews. Search Everywhere Optimization is standaard inbegrepen.'
}, {
  title: 'Je ziet al in maand 1 wat je markt doet',
  desc: 'Al tijdens de DataSprint zie je live welke boodschappen aanslaan bij jouw doelgroep.'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    x: 40,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};
function AnimatedCounter() {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!inView) return;
    const duration = 2000;
    const target = 90;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [inView]);
  return <span ref={ref} data-id="element-258">{count}</span>;
}
export function HowItWorksForYou() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-20 bg-white" data-id="element-259">
      <div className="max-w-[1200px] mx-auto px-6" data-id="element-260">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-start" data-id="element-261">
          {/* Left */}
          <motion.div className="lg:sticky lg:top-32" initial={{
          opacity: 0,
          x: -40
        }} animate={inView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.8,
          type: 'spring',
          stiffness: 80
        }} data-id="element-262">
            <div className="section-label" data-id="element-263">Voor jou</div>
            <h2 className="text-[clamp(2.2rem,4vw,3.2rem)] font-black text-brand-dark leading-tight tracking-tight mb-5" data-id="element-264">
              Wat de sonar-aanpak
              <br data-id="element-265" />
              <span className="text-brand-primary" data-id="element-266">voor jou doet</span>
            </h2>
            <p className="text-brand-gray text-[1.08rem] leading-relaxed mb-8" data-id="element-267">
              Elke beslissing die wij nemen is gebaseerd op wat bewezen werkt in
              jouw specifieke markt. Niet op trends, niet op aannames.
            </p>

            {/* Visual stat */}
            <motion.div className="rounded-3xl p-7 border border-[#E8EDF8] bg-brand-light mb-5" whileHover={{
            scale: 1.02,
            boxShadow: '0 12px 48px rgba(0, 82, 204, 0.1)',
            transition: {
              type: 'spring',
              stiffness: 300,
              damping: 20
            }
          }} data-id="element-268">
              <div className="text-[3.5rem] font-black text-brand-primary leading-none mb-3" data-id="element-269">
                <AnimatedCounter data-id="element-270" />
              </div>
              <div className="text-brand-dark font-black text-lg mb-2" data-id="element-271">
                dagen DataSprint
              </div>
              <div className="text-brand-gray text-[0.95rem] leading-relaxed" data-id="element-272">
                Daarna weet je exact wat werkt. Dan pas bouwen we. Niet eerder.
              </div>
            </motion.div>

            {/* Trust indicators */}
            <div className="rounded-3xl p-7 border border-[#E8EDF8] bg-white mb-5" data-id="element-273">
              <div className="flex items-center gap-3 mb-4" data-id="element-274">
                <div className="w-2 h-2 rounded-full bg-[#28C840]" style={{
                boxShadow: '0 0 8px #28C840'
              }} data-id="element-275" />
                <span className="text-brand-dark font-bold text-sm" data-id="element-276">
                  Bewezen bij B2B-bedrijven
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-277">
                <div data-id="element-278">
                  <div className="text-2xl font-black text-brand-primary" data-id="element-279">
                    +280%
                  </div>
                  <div className="text-brand-gray text-xs mt-1" data-id="element-280">
                    meer aanvragen
                  </div>
                </div>
                <div data-id="element-281">
                  <div className="text-2xl font-black text-brand-primary" data-id="element-282">
                    −67%
                  </div>
                  <div className="text-brand-gray text-xs mt-1" data-id="element-283">
                    kosten per lead
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <motion.a href="#gesprek" className="btn-primary w-full justify-center" whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.97
          }} data-id="element-284">
              <CalendarIcon size={18} data-id="element-285" />
              Plan een gratis presentatie
              <ArrowRightIcon size={16} data-id="element-286" />
            </motion.a>
          </motion.div>

          {/* Right: benefits */}
          <motion.div className="space-y-3" variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-287">
            {benefits.map(b => <motion.div key={b.title} className="flex gap-4 p-5 rounded-2xl border border-[#E8EDF8] bg-white group cursor-default" variants={itemVariants} whileHover={{
            y: -4,
            boxShadow: '0 12px 48px rgba(0, 82, 204, 0.08)',
            transition: {
              type: 'spring',
              stiffness: 300,
              damping: 20
            }
          }} data-id="element-288">
                <motion.div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center mt-0.5" style={{
              background: '#EFF6FF'
            }} whileHover={{
              scale: 1.1,
              rotate: 5,
              transition: {
                type: 'spring',
                stiffness: 300
              }
            }} data-id="element-289">
                  <CheckIcon size={16} className="text-brand-primary" strokeWidth={3} data-id="element-290" />
                </motion.div>
                <div data-id="element-291">
                  <div className="font-black text-brand-dark mb-1 group-hover:text-brand-primary transition-colors duration-300 text-[0.98rem]" data-id="element-292">
                    {b.title}
                  </div>
                  <div className="text-brand-gray text-[0.9rem] leading-relaxed" data-id="element-293">
                    {b.desc}
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}